import { isNil, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from 'react-icons/ai';
import { IoIosArrowBack } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import {
  setDashboardCalendarTargetDate,
  setDashboardMobileSideMenuOpen,
} from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import CalendarPopupMenu from '../../layout/Navbar/CalendarPopupMenu';
import UserMenu from '../../layout/Navbar/UserMenu';

const DashboardSideMenuControlPanel = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { navigate, isSameRoute, isChildRoute } = useAppNavigation();
  const { isDashboardMobileSideMenuOpen } = useSelector(
    (state: RootReducerType) => state.ui,
  );
  const { getPartnerContactDetailsResponse } = useSelector(
    (state: RootReducerType) => state.dashboard,
  );

  const setToday = () => {
    const now = new Date();
    dispatch(setDashboardCalendarTargetDate(now.getTime()));
  };

  const isAppointmentsPage = useMemo<boolean>(() => {
    let result = false;
    if (!isNil(location)) {
      if (
        isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS) ||
        isSameRoute(location.pathname, ROUTES.HOME_ROUTE) ||
        isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE) ||
        location.pathname === `/${i18n.language}/`
      ) {
        result = true;
      }
    }

    return result;
  }, [location, isSameRoute, i18n.language]);

  const isBackButton = useMemo<boolean>(() => {
    if (location) {
      if (
        isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)
      ) {
        return true;
      }

      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TREATMENTS)) {
        return true;
      }
    }

    return false;
  }, [location]);

  const isUserMenuAvailable = useMemo<boolean>(() => {
    if (isAppointmentsPage) {
      return false;
    }

    return true;
  }, [isAppointmentsPage]);

  const isDefaultBackBtn = useMemo<boolean>(() => {
    if (location) {
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)) {
        return true;
      }

      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
        return true;
      }

      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
        return true;
      }

      if (
        isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)
      ) {
        return true;
      }

      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TREATMENTS)) {
        return true;
      }
    }

    return false;
  }, [location, isChildRoute]);

  const sectionTitle = useMemo<string>(() => {
    if (location) {
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)) {
        return t('static.dashboard.side_menu_item_label_appointments');
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
        return t('static.dashboard.side_menu_item_label_clients');
      }
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
        if (getPartnerContactDetailsResponse?.data) {
          return startCase(
            t('static.dashboard.side_menu_item_label_contact_details', {
              name:
                `${getPartnerContactDetailsResponse.data.first_name} ${getPartnerContactDetailsResponse.data.last_name}` ||
                '',
            }),
          );
        }
        return '';
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
        return t('static.dashboard.side_menu_item_label_services');
      }
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
        return t('static.dashboard.side_menu_item_label_service_details');
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)) {
        return t('static.dashboard.side_menu_item_label_businesses');
      }
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_PROFILE)) {
        return t('static.dashboard.side_menu_item_label_preferences_profile');
      }
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)) {
        return t('static.dashboard.side_menu_item_label_business_details');
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_OVERVIEW)) {
        return t('static.dashboard.side_menu_item_label_overview');
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SETTINGS)) {
        return t('static.dashboard.side_menu_item_label_preferences');
      }
      if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)) {
        return t('static.dashboard.side_menu_item_label_appointments');
      }
      if (
        isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)
      ) {
        return t('static.dashboard.side_menu_item_label_appointment_details');
      }
      if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TREATMENTS)) {
        return t('static.dashboard.side_menu_item_label_treatment_details');
      }
      if (isSameRoute(location.pathname, ROUTES.HOME_ROUTE)) {
        return t('static.dashboard.side_menu_item_label_appointments');
      }
      if (
        isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_TEMP_CLOSING_HOURS,
        ) ||
        isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_TEMP_OPENING_HOURS,
        )
      ) {
        return t('static.dashboard.side_menu_item_label_temp_hours');
      }
      if (isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE)) {
        return t('static.dashboard.side_menu_item_label_chat');
      }
    }

    return '';
  }, [location, getPartnerContactDetailsResponse, i18n.language]);

  const isBackVisible = useMemo<boolean>(() => {
    if (location.key === 'default') {
      return false;
    }

    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TREATMENTS)) {
      return true;
    }

    return false;
  }, [location]);

  const onMobileSideMenuToggle = useCallback(() => {
    if (isBackButton) {
      navigate(-1);
    } else {
      dispatch(setDashboardMobileSideMenuOpen(!isDashboardMobileSideMenuOpen));
    }
  }, [isDashboardMobileSideMenuOpen, isBackButton, dispatch, navigate]);

  const onBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="relative z-10 flex max-h-[58px] min-h-[58px] flex-row items-start justify-start shadow">
      {isDefaultBackBtn ? (
        <button
          className={`z-20 flex max-h-[58px] min-h-[58px] min-w-[44px] max-w-[44px] cursor-pointer flex-col items-center justify-center rounded-br-3xl hover:bg-slate-500 hover:text-white hover:lg:bg-white hover:lg:text-sky-600 ${
            isBackVisible ? '' : 'lg:hidden'
          }`}
          onClick={onBack}
          id="dashboard-side-menu-open-btn"
          key="dashboard-side-menu-open-btn"
          aria-label="dashboard-side-menu-open-btn"
          data-test-id="dashboard-side-menu-open-btn"
          data-umami-event="dashboard-side-menu-open-btn-click"
          type="button"
        >
          <div className="flex min-h-full grow flex-col items-center justify-center">
            <IoIosArrowBack size={24} />
          </div>
        </button>
      ) : (
        <>
          <button
            className={`${
              isDashboardMobileSideMenuOpen ? 'hidden' : 'flex'
            } z-20 max-h-[58px] min-h-[58px] min-w-[44px] max-w-[44px] cursor-pointer flex-col items-center justify-center rounded-br-3xl hover:bg-slate-500 hover:text-white hover:lg:bg-white hover:lg:text-sky-600 ${
              isBackVisible ? '' : 'lg:hidden'
            }`}
            onClick={onMobileSideMenuToggle}
            id="dashboard-side-menu-open-btn"
            key="dashboard-side-menu-open-btn"
            aria-label="dashboard-side-menu-open-btn"
            data-test-id="dashboard-side-menu-open-btn"
            data-umami-event="dashboard-side-menu-open-btn-click"
            type="button"
          >
            <div className="flex min-h-full grow flex-col items-center justify-center">
              {isBackButton ? (
                <IoIosArrowBack size={24} />
              ) : (
                <AiOutlineMenuUnfold size={24} />
              )}
            </div>
          </button>
          <button
            className={`${
              isDashboardMobileSideMenuOpen ? 'flex' : 'hidden'
            } z-20 max-h-[58px] min-h-[58px] min-w-[44px] max-w-[44px] cursor-pointer flex-col items-center justify-center rounded-br-3xl bg-slate-500 text-white hover:text-black`}
            onClick={onMobileSideMenuToggle}
            id="dashboard-side-menu-close-btn"
            key="dashboard-side-menu-close-btn"
            aria-label="dashboard-side-menu-close-btn"
            data-test-id="dashboard-side-menu-close-btn"
            data-umami-event="dashboard-side-menu-close-btn-click"
            type="button"
          >
            <div className="mb-3 mr-3 pl-2 pt-2">
              <AiOutlineMenuFold size={24} />
            </div>
          </button>
        </>
      )}
      {isAppointmentsPage ? (
        <button
          className="absolute top-0 flex min-h-full min-w-full cursor-pointer flex-row items-center justify-center px-10 text-center text-lg font-bold"
          type="button"
          data-test-id="dashboard-calendar-today-btn"
          id="dashboard-calendar-today-btn"
          key="dashboard-calendar-today-btn"
          aria-label="dashboard-calendar-today-btn"
          onClick={setToday}
        >
          <span className="cursor-pointer rounded-full p-2 px-4 hover:bg-white hover:text-slate-500 text-white border bg-slate-500 border-slate-500 text-sm">
            {t('static.dashboard.appointments_section.today_shortcut')}
          </span>
        </button>
      ) : (
        <span className="absolute top-0 flex min-h-full min-w-full flex-row items-center justify-center px-10 text-center text-lg font-bold">
          {sectionTitle}
        </span>
      )}
      {isAppointmentsPage && <CalendarPopupMenu />}
      <span className="z-20 hidden grow flex-row items-center justify-end p-2 lg:flex">
        <UserMenu />
      </span>
    </div>
  );
};

export default DashboardSideMenuControlPanel;
